import { AnalyticsPage } from './analytics/AnalyticsPage'
import { AuditLogContent, AuditLogPage } from './logs/AuditLogPage'
import { SystemLogsPage } from './logs/SystemLogsPage'
import { ReportsPage } from './reports/ReportsPage'
import { StatisticsContent, StatisticsPage } from './statistics/StatisticsPage'

/**
 * `manager-reporting` — what the trade looked like, and what the system was doing.
 *
 * All five ported: statistics, analytics, reports, audit-log, system-logs.
 *
 * **Five spellings for one date range, across this one module.** Sending the wrong pair never
 * fails: the parameter is ignored and the endpoint answers over its own default window with a
 * perfectly plausible number.
 *
 * | Spelling | Endpoints |
 * |---|---|
 * | `date_from` / `date_to` | `/statistics/sales`, `/categories`, `/products` |
 * | `from` / `to` | `/orders/heatmap`, `/employees/performance`, `/analytics/revenue`, `/analytics/day-hour-revenue`, `/financial-summary/detailed`, `/reports/vat-input` |
 * | `dateFrom` / `dateTo` | `/analytics/sla`, `/employees/wallets` |
 * | `days` | six analytics endpoints, `/reports/waste`, `/reports/stock-turnover`, and more |
 * | `years` or `fromYear` / `toYear` | `/financial-summary/yearly` |
 *
 * Measured cost of getting one wrong: `/analytics/sla?from=…` answers 2 366 orders where
 * `?dateFrom=…` answers 2 625; `/employees/wallets?from=…` answers **208 110,45 €** over the whole
 * year where `?dateFrom=…` answers **44 764,80 €** for July — 4,65× what staff actually owe, in
 * cash they hand over. `statisticsParams`, `rangeParams`, `fromToParams`, `slaParams` and
 * `dayCountParams` exist so a caller cannot mix them up, and each is pinned by a test.
 *
 * **The two log endpoints are not alike either.** `/audit-logs` answers a `total` and takes
 * `limit` / `offset`; `/system-logs` takes neither and cannot be paged at all. `auditQuery`
 * and `systemQuery` are typed to their own endpoint's parameters, and each page states which
 * of the two it is doing rather than implying the list is everything.
 */
export const managerReportingModule = {
  name: 'manager-reporting',
  routes: [
    {
      // Not in the sidebar; legacy reaches it by direct link and through `/analytics`.
      path: '/statistics',
      page: 'statistics',
      guard: { permission: 'statistics' },
      component: StatisticsPage,
    },
    {
      // *Analytika* in the sidebar. Its five tabs each carry their own tier gate except the first,
      // which is `/statistics` embedded whole and stays open on every tier — legacy's own rule.
      // Every endpoint behind it is `@RequireAuth({"main_admin", "admin"})` on top of the flag.
      path: '/analytics',
      page: 'analytics',
      guard: { permission: 'analytics', franchiseModule: 'analytics' },
      component: AnalyticsPage,
    },
    {
      // Not in the sidebar in legacy either — reached from `/system-logs`, and by direct link.
      // `/audit-logs` is `@RequireAuth({"main_admin", "admin"})`, so the gate is the role and not a
      // permission flag: a `waiter` holding `system_logs` would be refused by the server anyway.
      path: '/audit-log',
      page: 'audit-log',
      guard: { systemRoles: ['main_admin', 'admin'] },
      component: AuditLogPage,
    },
    {
      // *Prehľad firmy* in the sidebar. Legacy dropped its own sales, products and customers tabs in
      // favour of `/analytics`; what is left is the financial and inventory half.
      path: '/reports',
      page: 'reports',
      guard: { permission: 'company_overview', franchiseModule: 'reports' },
      component: ReportsPage,
    },
    {
      // The sidebar shows this as *Logy* behind `system_logs` + the `system_logs` tier module; the
      // route mirrors the server's role gate. Both are honoured — narrower of the two wins.
      path: '/system-logs',
      page: 'system-logs',
      guard: { systemRoles: ['main_admin', 'admin'] },
      component: SystemLogsPage,
    },
  ],
} as const

export {
  AnalyticsPage,
  AuditLogContent,
  AuditLogPage,
  ReportsPage,
  StatisticsContent,
  StatisticsPage,
  SystemLogsPage,
}
